// ============================================================================
// Collapsible section header for the order detail page.
//
// The page used to be one uninterrupted scroll: every `Divider` header was
// followed by a section that could not be shut. On an order with a dozen lines
// and half a dozen allotments, the thing an operator came to look at was five
// screens down. Each section now closes, and the open/closed choice is
// remembered per section (not per order) so the page opens the same way twice.
// ============================================================================
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { Space, Typography } from "antd";
import React, { useCallback, useState } from "react";

const storageKey = (key: string) => `oms.detail.section.${key}`;

const readStored = (key: string, fallback: boolean): boolean => {
  try {
    const raw = window.localStorage.getItem(storageKey(key));
    return raw === null ? fallback : raw === "1";
  } catch {
    return fallback;
  }
};

const DetailSection: React.FC<{
  /** Stable id for the remembered open/closed state. */
  sectionKey: string;
  title: React.ReactNode;
  /** Shown next to the title whether the section is open or shut — a closed section still flags itself. */
  summary?: React.ReactNode;
  /** Sits on the header row, right-aligned; clicks do not toggle the section. */
  extra?: React.ReactNode;
  defaultOpen?: boolean;
  /** Overrides both the remembered state and `defaultOpen` — for a deep link that must land on
      something visible (a closed section cannot be scrolled into view). */
  forceOpen?: boolean;
  children: React.ReactNode;
}> = ({ sectionKey, title, summary, extra, defaultOpen = true, forceOpen, children }) => {
  const [stateOpen, setOpen] = useState(() => readStored(sectionKey, defaultOpen));
  const open = forceOpen || stateOpen;

  const toggle = useCallback(() => {
    setOpen(() => {
      const next = !open;
      try {
        window.localStorage.setItem(storageKey(sectionKey), next ? "1" : "0");
      } catch {
        /* private mode / blocked storage — the section still toggles for this visit */
      }
      return next;
    });
  }, [sectionKey, open]);

  return (
    <section style={{ marginTop: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: 6,
          marginBottom: open ? 12 : 0,
        }}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle();
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            userSelect: "none",
            flex: 1,
            minWidth: 0,
          }}
        >
          <span style={{ color: "#8c8c8c", fontSize: 12 }}>
            {open ? <DownOutlined /> : <RightOutlined />}
          </span>
          <Typography.Text strong style={{ fontSize: 15 }}>
            {title}
          </Typography.Text>
          {summary ? <Space size={6}>{summary}</Space> : null}
        </div>
        {extra ? (
          <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
            {extra}
          </div>
        ) : null}
      </div>
      {/* Kept mounted: collapsing a section must not throw away a table's scroll/sort or an
          open inline control inside it. */}
      <div style={{ display: open ? undefined : "none" }}>{children}</div>
    </section>
  );
};

export default DetailSection;
